/*
 * Bag v2 — behaviour for the reference store's bag mechanics (Gymshark, recorded 2026-09-29).
 * Styling: assets/runit-bag-v2.css. Kill switch: PostHog `kill-aov-bag-v2` → `rl-off-bag-v2` on
 * <html>; every handler checks it at the moment of use, so turning it on restores today's bag.
 *
 * What it adds on top of the theme's own drawer (it never replaces the theme's cart calls):
 *   1. Remove → "You removed an item from your bag. Undo" (re-adds the removed lines, including a
 *      personalisation line that left with its garment).
 *   2. "+ Add" on a companion card opens a size sheet instead of three dropdowns on the card.
 *   3. Product page: no size is pre-selected. Add to cart asks for one — "Pick a size" on
 *      desktop, the size sheet on a phone.
 *   4. The discount code is always open; the bag header gets a shadow while its body scrolls.
 * The cart page (/cart) gets 1 and 4 too: its <cart-items> and footer are watched like the drawer.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var DESKTOP = window.matchMedia('(min-width: 990px)');
  var PHONE = window.matchMedia('(max-width: 749px)');
  var ADD_ICON = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1v14M1 8h14" stroke="currentColor" stroke-width="1.6" fill="none"/></svg>';
  var SPIN_ICON = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5a6.5 6.5 0 1 0 6.5 6.5" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>';
  var TICK_ICON = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 8.5l3.5 3.5 7.5-8" stroke="currentColor" stroke-width="1.8" fill="none"/></svg>';

  function on() {
    return !root.classList.contains('rl-off-bag-v2');
  }
  function qs(sel, scope) { return (scope || document).querySelector(sel); }
  function qsa(sel, scope) { return Array.prototype.slice.call((scope || document).querySelectorAll(sel)); }
  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function drawer() { return qs('cart-drawer'); }

  /* ------------------------------------------------------------ snackbox */

  function snack(message, opts) {
    opts = opts || {};
    qsa('.rl-snack').forEach(function (s) { s.remove(); });
    var el = document.createElement('div');
    el.className = 'rl-snack' + (opts.inBag ? ' rl-snack--in-bag' : '');
    el.setAttribute('role', 'alertdialog');
    el.setAttribute('aria-live', 'polite');
    el.innerHTML = (opts.image ? '<img class="rl-snack__img" alt="" src="' + esc(opts.image) + '">' : '') +
      '<p class="rl-snack__text"></p>' +
      (opts.action ? '<button type="button" class="rl-snack__action"></button>' : '');
    qs('.rl-snack__text', el).textContent = message;
    if (opts.action) {
      var b = qs('.rl-snack__action', el);
      b.textContent = opts.action;
      b.addEventListener('click', function () { el.remove(); opts.onAction(); });
    }
    // cart-drawer itself, not its #CartDrawer child: the theme replaces that child on every re-render
    var host = opts.inBag ? drawer() : document.body;
    (host || document.body).appendChild(el);
    if (opts.inBag) { // sit just above the checkout bar, whatever its height on this device
      var foot = qs('.drawer__footer', drawer());
      if (foot) el.style.bottom = (foot.getBoundingClientRect().height + 12) + 'px';
    }
    setTimeout(function () { el.remove(); }, 5300);
  }

  /* ------------------------------------------------------------ cart state + Undo */

  var lastCart = null;
  var pendingRemove = 0;
  var lastRestore = null;
  var pendingBase = null; // the cart as it was when the removal was tapped

  function readCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { if (c) lastCart = c; return c; })
      .catch(function () { return null; });
  }

  function lineRef(l) { return l.variant_id + '|' + JSON.stringify(l.properties || {}); }

  var linesSig = '';
  function currentLinesSig() {
    return qsa('cart-drawer .cart-item .quantity__input, cart-items .cart-item .quantity__input').map(function (i) {
      return i.getAttribute('data-quantity-variant-id') + ':' + i.value;
    }).join(',');
  }

  function afterDrawerRender() {
    var sig = currentLinesSig();
    if (sig === linesSig && !pendingRemove) return; // nothing in the bag changed: no cart read
    linesSig = sig;
    var before = lastCart;
    readCart().then(function (now) {
      if (!now || !pendingRemove || Date.now() - pendingRemove > 6000) return;
      var base = pendingBase || before;
      if (!base) return;
      /* Shopify re-keys lines when discounts re-allocate, so compare by what the line *is*
         (variant + properties) and by quantity, not by key. */
      var have = {};
      now.items.forEach(function (l) { have[lineRef(l)] = (have[lineRef(l)] || 0) + l.quantity; });
      var gone = [];
      base.items.forEach(function (l) {
        var left = have[lineRef(l)] || 0;
        var lost = l.quantity - left;
        have[lineRef(l)] = Math.max(0, left - l.quantity);
        if (lost > 0) gone.push(Object.assign({}, l, { quantity: lost }));
      });
      // a render can land before Shopify has applied the change; keep waiting for the next one
      if (!gone.length || !on()) return;
      pendingRemove = 0;
      pendingBase = null;
      var inBag = !!(drawer() && drawer().classList.contains('active'));
      snack('You removed an item from your bag.', {
        inBag: inBag,
        image: gone[0].image || '',
        action: 'Undo',
        onAction: function () { lastRestore = gone; restore(gone); }
      });
    });
  }

  function restore(lines) {
    var items = lines.map(function (l) {
      var item = { id: l.variant_id, quantity: l.quantity, properties: l.properties || {} };
      if (l.selling_plan_allocation && l.selling_plan_allocation.selling_plan) {
        item.selling_plan = l.selling_plan_allocation.selling_plan.id;
      }
      return item;
    });
    return addItems(items).catch(function () {
      location.reload();
    });
  }

  // Adds lines and re-renders the drawer, bag icon, totals and delivery bar from the same response.
  // Rejects with Shopify's message when the add fails (sold out, limits), changing nothing.
  function addItems(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items, sections: 'cart-drawer,cart-icon-bubble', sections_url: location.pathname })
    })
      .then(function (r) { return r.json().then(function (j) { if (!r.ok || j.status) throw new Error(j.description || j.message || 'Could not add to bag'); return j; }); })
      .then(function (state) {
        if (/^\/cart\/?$/.test(location.pathname)) { location.reload(); return state; } // the cart page renders its own sections
        // The lines are in the bag now: a render hiccup must not read as a failed add (a retry would add twice).
        try {
          var d = drawer();
          if (d && state && state.sections && typeof d.renderContents === 'function') {
            d.classList.remove('is-empty'); // add.js has no item_count; renderContents keeps the empty grid otherwise
            d.renderContents(state, true);
          }
          /* global publish, PUB_SUB_EVENTS */
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state }); // 'cart-items': cart-drawer-items skips its own re-fetch, which filled the drawer with the /cart page table
          }
        } catch (e) {}
        return state;
      });
  }

  // A minus at one (or the theme's remove button) is a removal: remember the cart as it was.
  document.addEventListener('click', function (e) {
    if (!on()) return;
    var minus = e.target.closest('cart-drawer .quantity__button--minus, cart-items .quantity__button--minus');
    var remove = e.target.closest('cart-drawer cart-remove-button, cart-items cart-remove-button');
    if (minus) {
      var input = qs('.quantity__input', minus.parentElement);
      if (input && Number(input.value) <= 1 && String(input.min) === '0') { pendingRemove = Date.now(); pendingBase = lastCart; }
    }
    if (remove) { pendingRemove = Date.now(); pendingBase = lastCart; }
  }, true);

  /* ------------------------------------------------------------ drawer polish, re-applied per render */

  function enhanceDrawer() {
    if (!on()) return;
    // drawer and cart page alike
    qsa('details.rl-cart-discount__details').forEach(function (det) {
      det.open = true;
      var h = qs('.rl-cart-discount__heading', det);
      if (h && h.textContent.trim() !== 'Discount code') h.textContent = 'Discount code';
    });
    var d = drawer();
    if (!d) return;
    /* A companion with options keeps its own "+ Add" disabled until every option is really chosen
       (assets/runit-addon-choice.js, defect OBS01) and a disabled button receives no clicks. So the
       card gets a visible "+ Add" of ours that opens the size sheet; the sheet then makes the choice
       through the gate's own selects and presses the theme's button once the gate enables it. */
    qsa('cart-drawer-upsell', d).forEach(function (card) {
      var btn = qs('.upsell__add-btn', card);
      // A keychain card links to its page ("Personalise"); in the bag it opens the keychain builder instead.
      if (!btn || !(qs('select.rl-addon-choice__select', card) || btn.hasAttribute('data-rl-keychain')) || qs('.rl-upsell-add', card)) return;
      var ours = document.createElement('button');
      ours.type = 'button';
      ours.className = 'rl-upsell-add';
      ours.innerHTML = '<span aria-hidden="true">+</span> Add';
      ours.setAttribute('aria-label', 'Add ' + ((qs('.upsell__title', card) || {}).textContent || '').trim() + (btn.hasAttribute('data-rl-keychain') ? ' — choose your design' : ' — choose a size'));
      card.setAttribute('data-rl-has-sheet', '');
      btn.parentNode.insertBefore(ours, btn);
    });
    var inner = qs('.drawer__inner', d);
    var body = qs('.cart-drawer__body', d);
    if (inner && body && !body.__rlScroll) {
      body.__rlScroll = true;
      body.addEventListener('scroll', function () {
        inner.classList.toggle('rl-bag-scrolled', body.scrollTop > 0);
      }, { passive: true });
    }
  }

  var renderTimer = 0;
  var watchedDrawer = null;
  function watchDrawer() {
    var d = drawer();
    watchedDrawer = d;
    // the drawer, plus the cart page's lines and footer (/cart re-renders those, not the drawer)
    var targets = [d, qs('cart-items'), qs('#main-cart-footer')].filter(Boolean);
    if (!targets.length) return;
    /* Throttled, not debounced: the drawer mutates continuously (countdown ticks, heart syncs),
       and a debounce that resets on every mutation would never run. */
    var mo = new MutationObserver(function () {
      if (renderTimer) return;
      renderTimer = setTimeout(function () { renderTimer = 0; enhanceDrawer(); afterDrawerRender(); }, 120);
    });
    targets.forEach(function (t) { mo.observe(t, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] }); });
    linesSig = currentLinesSig();
    enhanceDrawer();
    readCart();
  }

  /* ------------------------------------------------------------ size sheet */

  var sheetEl = null;

  function closeSheet(now) {
    var el = sheetEl;
    if (!el) return Promise.resolve();
    sheetEl = null;
    document.removeEventListener('keydown', escClose);
    if (now) { el.remove(); return Promise.resolve(); }
    el.classList.add('is-closing');
    return wait(400).then(function () { el.remove(); });
  }
  function escClose(e) { if (e.key === 'Escape') closeSheet(); }

  /*
   * spec: { title, meta, price, image, groups: [{label, values, selected, onPick}],
   *         sizes: [{label, disabled}], onSize(label, row) -> Promise,
   *         body: Element (in place of the size list: the keychain builder) }
   */
  function openSheet(spec) {
    closeSheet(true);
    qsa('.rl-sheet').forEach(function (old) { old.remove(); }); // one still animating out: never two keychain forms
    var el = document.createElement('div');
    el.className = 'rl-sheet';
    el.innerHTML =
      '<div class="rl-sheet__panel" role="dialog" aria-modal="true" aria-label="Add to bag">' +
        '<button type="button" class="rl-sheet__close" aria-label="Close"><svg viewBox="0 0 16 16" width="18" height="18" aria-hidden="true"><path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="1.6"/></svg></button>' +
        '<p class="rl-sheet__title">Add to bag</p>' +
        '<div class="rl-sheet__product">' +
          (spec.image ? '<img alt="" src="' + esc(spec.image) + '">' : '') +
          '<div><p class="rl-sheet__name"></p>' + (spec.meta ? '<p class="rl-sheet__meta"></p>' : '') + '<p class="rl-sheet__price"></p></div>' +
        '</div>' +
        '<div class="rl-sheet__groups"></div>' +
        (spec.body ? '<div class="rl-sheet__body"></div>' :
        '<p class="rl-sheet__label" style="padding:0 1.6rem">Select size</p>' +
        '<ul class="rl-sheet__sizes"></ul>') +
      '</div>';
    qs('.rl-sheet__name', el).textContent = spec.title || '';
    if (spec.meta) qs('.rl-sheet__meta', el).textContent = spec.meta;
    qs('.rl-sheet__price', el).innerHTML = spec.price || '';
    var groups = qs('.rl-sheet__groups', el);
    (spec.groups || []).forEach(function (g) {
      var wrap = document.createElement('div');
      wrap.className = 'rl-sheet__group';
      wrap.innerHTML = '<p class="rl-sheet__label"></p><div class="rl-sheet__pills"></div>';
      qs('.rl-sheet__label', wrap).textContent = g.label;
      var pills = qs('.rl-sheet__pills', wrap);
      g.values.forEach(function (v) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'rl-sheet__pill';
        b.textContent = v;
        b.setAttribute('aria-pressed', String(v === g.selected));
        b.addEventListener('click', function () {
          qsa('.rl-sheet__pill', pills).forEach(function (p) { p.setAttribute('aria-pressed', String(p === b)); });
          g.onPick(v);
        });
        pills.appendChild(b);
      });
      groups.appendChild(wrap);
    });
    if (spec.body) qs('.rl-sheet__body', el).appendChild(spec.body);
    var list = qs('.rl-sheet__sizes', el);
    (spec.body ? [] : spec.sizes || []).forEach(function (s) {
      var li = document.createElement('li');
      li.innerHTML = '<button type="button" class="rl-sheet__size"><span></span><span class="rl-sheet__add">Add to bag ' + ADD_ICON + '</span></button>';
      var row = qs('button', li);
      qs('span', row).textContent = s.label;
      if (s.disabled) { row.disabled = true; qs('.rl-sheet__add', row).textContent = 'Sold out'; }
      row.addEventListener('click', function () {
        if (row.classList.contains('is-adding')) return;
        row.classList.add('is-adding');
        qs('.rl-sheet__add', row).innerHTML = 'Adding ' + SPIN_ICON;
        var note = qs('.rl-sheet__error', el);
        if (note) note.remove();
        Promise.resolve(spec.onSize(s.label, row)).then(function (ok) {
          if (ok === false || typeof ok === 'string') {
            row.classList.remove('is-adding');
            qs('.rl-sheet__add', row).innerHTML = 'Add to bag ' + ADD_ICON;
            if (ok && sheetEl === el) {
              note = document.createElement('p');
              note.className = 'rl-sheet__error rl-size-error';
              note.setAttribute('role', 'alert');
              note.textContent = ok;
              list.before(note);
            }
            return;
          }
          qs('.rl-sheet__add', row).innerHTML = 'Added ' + TICK_ICON;
          return wait(450).then(function () { return closeSheet(); });
        });
      });
      list.appendChild(li);
    });
    el.addEventListener('click', function (e) {
      if (e.target === el || e.target.closest('.rl-sheet__close')) closeSheet();
    });
    document.addEventListener('keydown', escClose);
    document.body.appendChild(el);
    sheetEl = el;
    setTimeout(function () { var c = qs('.rl-sheet__close', el); if (c) c.focus(); }, 50);
  }

  /* ------------------------------------------------------------ companion cards: "+ Add" → sheet */

  function setSelect(select, value) {
    if (!select) return;
    select.value = value; // always announce: the gate counts an option only after a change event
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Re-reads /cart.js until test(cart) holds or ms runs out.
  function pollCart(test, ms) {
    var until = Date.now() + (ms || 6000);
    return new Promise(function (resolve) {
      (function tick() {
        readCart().then(function (c) {
          if (c && test(c)) return resolve(true);
          if (Date.now() > until) return resolve(false);
          setTimeout(tick, 400);
        });
      })();
    });
  }

  function waitFor(test, ms) {
    var until = Date.now() + (ms || 3000);
    return new Promise(function (resolve) {
      (function tick() {
        if (test()) return resolve(true);
        if (Date.now() > until) return resolve(false);
        setTimeout(tick, 50);
      })();
    });
  }

  document.addEventListener('click', function (e) {
    if (!on()) return;
    var ours = e.target.closest('cart-drawer cart-drawer-upsell .rl-upsell-add');
    if (!ours) return;
    var card = ours.closest('cart-drawer-upsell');
    var btn = qs('.upsell__add-btn', card);
    var selects = qsa('select.rl-addon-choice__select', card);
    if (!btn || !selects.length || btn.hasAttribute('data-rl-keychain')) return; // a keychain gets its builder (below)
    e.preventDefault();
    e.stopImmediatePropagation();
    var labelOf = function (s) { var l = s.parentElement && qs('label', s.parentElement); return l ? l.textContent.trim() : ''; };
    var sizeSelect = selects.filter(function (s) { return /size/i.test(labelOf(s)); })[0] || selects[0];
    var others = selects.filter(function (s) { return s !== sizeSelect; });
    var choices = function (s) { return qsa('option', s).filter(function (o) { return o.value !== ''; }); };
    others.forEach(function (s) { if (!s.value) setSelect(s, choices(s)[0].value); });
    var img = qs('.upsell__image img', card);
    openSheet({
      title: (qs('.upsell__title', card) || {}).textContent ? qs('.upsell__title', card).textContent.trim() : '',
      meta: (qs('.upsell__desc', card) || {}).textContent ? qs('.upsell__desc', card).textContent.trim() : '',
      price: (qs('.upsell__price .regular-price', card) || {}).innerHTML || '',
      image: img ? (img.currentSrc || img.src) : '',
      groups: others.map(function (s) {
        return { label: labelOf(s), values: choices(s).map(function (o) { return o.value; }), selected: s.value, onPick: function (v) { setSelect(s, v); } };
      }),
      sizes: choices(sizeSelect).map(function (o) { return { label: o.value, disabled: o.disabled }; }),
      onSize: function (label) {
        setSelect(sizeSelect, label);
        // the gate enables its button once the combination resolves (and revalidates stock)
        return waitFor(function () { return card.getAttribute('data-runit-addon-resolved') === 'true' || !btn.disabled; }, 3000).then(function (ready) {
          if (!ready || btn.disabled) return false;
          // Read the bag itself before and after: waiting for a drawer render to refresh lastCart
          // could time out after a successful add, leaving the sheet open on "Add to bag".
          return readCart().then(function (c) {
            var before = c ? c.item_count : -1;
            btn.click();
            return pollCart(function (now) { return now.item_count !== before; }, 6000);
          });
        });
      }
    });
  }, true);

  /* ------------------------------------------------------------ keychain card: "+ Add" → builder sheet */

  /* The product page's keychain builder in the sheet (sections/runit-keychain-sheet.liquid, rendered for
     the card's product; assets/runit-keychain-3d.js runs it): design, own text / Instagram, Print Type,
     a live preview and the same line properties. A fixed design with its own product on sale adds that
     product; before launch the Personalised Keychain takes the design as its property, as on its page. */
  var kcScript = null;
  function keychainBuilder() {
    if (window.RunitKeychain) return Promise.resolve();
    return kcScript || (kcScript = new Promise(function (ok, no) {
      var src = (qs('script[src*="runit-keychain-3d"]') || {}).src || window.__rlKcSrc;
      if (!src) return no(new Error('no builder'));
      var sc = document.createElement('script'); sc.src = src; sc.onload = ok; sc.onerror = function (err) { kcScript = null; sc.remove(); no(err); }; document.head.appendChild(sc);
    }));
  }

  document.addEventListener('click', function (e) {
    if (!on()) return;
    var ours = e.target.closest('cart-drawer cart-drawer-upsell .rl-upsell-add');
    var card = ours && ours.closest('cart-drawer-upsell');
    var link = card && qs('.upsell__add-btn[data-rl-keychain]', card);
    if (!link) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var body = document.createElement('div');
    body.className = 'rl-kc-sheet-wrap';
    body.innerHTML = '<p class="rl-kc-sheet__loading" role="status">Loading the keychain builder…</p>';
    var img = qs('.upsell__image img', card);
    openSheet({
      title: ((qs('.upsell__title', card) || {}).textContent || '').trim(),
      price: (qs('.upsell__price .regular-price', card) || {}).innerHTML || '',
      image: img ? (img.currentSrc || img.src) : '',
      body: body
    });
    var sheet = sheetEl, panel = qs('.rl-sheet__panel', sheet);
    panel.setAttribute('aria-label', 'Add a keychain to your bag');
    qs('.rl-sheet__name', sheet).setAttribute('data-kc-title-for', 'rl-kc-sheet-form');
    qs('.rl-sheet__price', sheet).setAttribute('data-kc-price-for', 'rl-kc-sheet-form');
    var sheetImg = qs('.rl-sheet__product img', sheet); if (sheetImg) sheetImg.setAttribute('data-kc-image-for', 'rl-kc-sheet-form');
    var url = new URL(link.getAttribute('href'), location.href);
    url.searchParams.set('section_id', 'runit-keychain-sheet');
    fetch(url.pathname + url.search, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        if (sheetEl !== sheet) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var form = qs('#rl-kc-sheet-form', doc);
        if (!form) throw new Error('no builder');
        var tag = qs('script[src*="runit-keychain-3d"]', doc); if (tag) window.__rlKcSrc = tag.src;
        body.innerHTML = '';
        qsa('style, link[rel=stylesheet]', doc).forEach(function (st) { body.appendChild(st); });
        body.appendChild(form);
        return keychainBuilder().then(function () {
          window.RunitKeychain.init();
          form.addEventListener('submit', function (ev) {
            // the builder's own check (empty text, a design still switching) ran first, in capture
            ev.preventDefault();
            var btn = qs('.rl-kc-sheet__add', form), lbl = qs('.rl-sheet__add', btn), msg = qs('[data-kc-msg]', form);
            if (btn.classList.contains('is-adding')) return;
            var props = {};
            new FormData(form).forEach(function (v, k) { var m = /^properties\[(.+)\]$/.exec(k); if (m && v !== '') props[m[1]] = v; });
            // No design on the line yet (the builder still loading, or it could not run): never add a blank keychain.
            if (!props.Design) { if (msg) { msg.textContent = 'Choose a design, or add your text.'; msg.hidden = false; } return; }
            btn.classList.add('is-adding'); lbl.innerHTML = 'Adding ' + SPIN_ICON;
            addItems([{ id: Number(new FormData(form).get('id')), quantity: 1, properties: props }])
              .then(function () { lbl.innerHTML = 'Added ' + TICK_ICON; return wait(450).then(function () { return closeSheet(); }); })
              .catch(function (err) {
                btn.classList.remove('is-adding'); lbl.innerHTML = 'Add to bag';
                if (msg) { msg.textContent = err.message; msg.hidden = false; }
              });
          });
        });
      })
      .catch(function () {
        if (sheetEl !== sheet) return;
        body.innerHTML = '<p class="rl-kc-sheet__loading" role="alert">The keychain builder could not load. <a href="' + esc(link.getAttribute('href')) + '">Personalise it on its page</a>.</p>';
      });
  }, true);

  /* ------------------------------------------------------------ product page: size first */

  // The size pills render unpicked (snippets/product-variant-picker.liquid) unless the shopper
  // arrived on an explicit ?variant= or only one size can be bought, so "no size" is simply "no
  // checked size radio". The form's hidden id still holds the first variant: nothing may submit it.
  function sizeOption() {
    return qs('[data-runit-size-option]');
  }
  function shown(el) { return !!el && el.getClientRects().length > 0; }
  // A set tier parks the page's picker (assets/runit-armour-bundles.js flags its own rows then).
  function sizeMissing(opt) {
    return shown(opt) && !qs('input[type=radio]:checked', opt) && qsa('input[type=radio]', opt).length > 0;
  }
  function formOf(opt) {
    var r = qs('input[type=radio]', opt);
    return (r && document.getElementById(r.getAttribute('form'))) || document;
  }
  // Express checkout buys the form's hidden id: hidden until a size is chosen.
  function syncExpress() {
    var opt = sizeOption();
    qsa('.rl-apple-pay', formOf(opt)).forEach(function (el) {
      el.toggleAttribute('data-rl-need-size', !qs('input[type=radio]:checked', opt));
    });
  }

  function setupSizeFirst() {
    var opt = sizeOption();
    if (!opt) return;
    syncExpress();
    opt.addEventListener('change', function () {
      opt.removeAttribute('data-rl-size-missing');
      var err = qs('.rl-size-error', opt);
      if (err) err.remove();
      syncExpress();
    });
  }

  function sizeLabel(radio) {
    return String(radio.value).trim();
  }
  function priceHtml(scope) {
    var p = qs('.price__sale .price-item--sale, .price-item--regular', scope);
    return p ? p.innerHTML : '';
  }
  // The visible Add to Bag: a set's own button (Just The One), else the page form's.
  function addButton() {
    return qsa('[data-bundle-add], .main-product-atc').filter(function (b) { return !b.closest('sticky-atc') && shown(b); })[0];
  }
  // The variant the page's picker now names, from its own JSON.
  function pickedVariant(opt) {
    var picker = opt.closest('variant-selects, variant-radios');
    var json = picker && qs('script[type="application/json"]', picker);
    var chosen = qsa('.product-form__input', picker).map(function (g) {
      var c = qs('input[type=radio]:checked', g) || qs('select', g);
      return c ? c.value : null;
    });
    try {
      return JSON.parse(json.textContent).filter(function (v) {
        return v.options.every(function (o, i) { return o === chosen[i]; });
      })[0] || null;
    } catch (e) { return null; }
  }
  // Resolves once the theme has finished the variant switch (it disables Add to Bag while it fetches
  // the new variant's section and swaps the form's id), or false after ms.
  function variantSettled(opt, button, ms) {
    var v = pickedVariant(opt);
    var input = qs('[name="id"]', formOf(opt));
    if (!v || !v.available) return Promise.resolve(false);
    return waitFor(function () {
      return input && String(input.value) === String(v.id) && !button.disabled && button.getAttribute('aria-disabled') !== 'true';
    }, ms);
  }
  // Presses Add to Bag, then: true when the bag opened or the garment's own line grew (a
  // personalisation's add-on lands first and must not count), 'pz' when the personaliser stopped
  // the add (it says why on the page), else an error to show.
  function addAndWait(button, variantId, ms) {
    var failed = false;
    function onFail() { failed = true; }
    function qty(c) {
      return c ? c.items.reduce(function (n, i) { return n + (String(i.variant_id) === String(variantId) ? i.quantity : 0); }, 0) : -1;
    }
    document.addEventListener('runit:pz-fail', onFail); // before the click: validation fails synchronously
    var until = Date.now() + ms;
    return readCart().then(function (c) {
      var before = qty(c);
      button.click();
      return new Promise(function (resolve) {
        (function tick() {
          var d = drawer();
          if (d && d.classList.contains('active')) return resolve(true);
          if (failed) return resolve('pz');
          var err = qs('.main-product-form .product-form__error-message-wrapper:not([hidden]) .product-form__error-message');
          if (err && err.textContent.trim()) return resolve(err.textContent.trim());
          if (Date.now() > until) return resolve('This size could not be added to your bag. Please try again.');
          readCart().then(function (now) {
            if (failed) return resolve('pz');
            if (before > -1 && qty(now) > before) return resolve(true);
            setTimeout(tick, 400);
          });
        })();
      });
    }).then(function (r) { document.removeEventListener('runit:pz-fail', onFail); return r; });
  }

  // Window capture: ahead of the page's own option gate (runit-tweaks.js, document capture), which
  // would only scroll; a set's sticky button forwards its click to [data-bundle-add], caught here too.
  window.addEventListener('click', function (e) {
    if (!on()) return;
    var opt = sizeOption();
    if (!sizeMissing(opt)) return;
    // Another choice (e.g. Print Finish, unpicked on arrival) is also missing: leave it to the page's
    // option gate (runit-tweaks.js), which points at the first missing group, rather than a size-only sheet.
    var sizeRadio = qs('input[type=radio]', opt);
    var formId = sizeRadio && sizeRadio.getAttribute('form');
    var names = {};
    qsa(formId ? 'input[type=radio][form="' + formId + '"]' : 'variant-selects input[type=radio]').forEach(function (r) {
      if (opt.contains(r) || !shown(r.closest('fieldset') || r.parentElement)) return;
      names[r.name] = names[r.name] || r.checked;
    });
    var otherMissing = Object.keys(names).some(function (n) { return !names[n]; });
    if (otherMissing) return;
    var atc = e.target.closest('.main-product-atc, sticky-atc button[type=submit], sticky-atc .sticky-atc__button, [data-rl-atc], [data-bundle-add]');
    if (!atc) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (!PHONE.matches) {
      if (!qs('.rl-size-error', opt)) {
        var err = document.createElement('p');
        err.className = 'rl-size-error';
        err.setAttribute('role', 'alert');
        err.textContent = 'Pick a size';
        opt.appendChild(err);
      }
      opt.setAttribute('data-rl-size-missing', '');
      opt.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var first = qs('input[type=radio]:not([disabled])', opt);
      if (first) first.focus({ preventScroll: true });
      return;
    }
    var radios = qsa('input[type=radio]', opt);
    var title = qs('.product__title h1, h1');
    var info = qs('.product__info-wrapper, product-info') || document;
    var media = qs('.product__media img, .product-media-container img');
    var picked = qsa('variant-selects input[type=radio]:checked, variant-radios input[type=radio]:checked')
      .filter(function (r) { return !opt.contains(r); })
      .map(function (r) { return r.value; });
    openSheet({
      title: title ? title.textContent.trim() : '',
      meta: picked.join(' · '),
      price: priceHtml(info),
      image: media ? (media.currentSrc || media.src) : '',
      sizes: radios.map(function (r) {
        return { label: sizeLabel(r), disabled: r.disabled || r.classList.contains('disabled') || r.hasAttribute('data-unavailable') };
      }),
      onSize: function (label) {
        var radio = radios.filter(function (r) { return sizeLabel(r) === label; })[0];
        var button = addButton();
        if (!radio || !button) return 'This size could not be added to your bag. Please try again.';
        radio.click();
        return variantSettled(opt, button, 10000).then(function (ready) {
          var v = ready && pickedVariant(opt);
          if (!v) return 'This size could not be added to your bag. Please try again.';
          return addAndWait(button, v.id, 20000);
        }).then(function (r) {
          // The personaliser said what is missing on the page (and scrolled to it).
          if (r === 'pz') { closeSheet(); return false; }
          return r;
        });
      }
    });
  }, true);

  /* ------------------------------------------------------------ boot */

  window.RunItBagV2 = { snack: snack, enhance: enhanceDrawer, watched: function () { return watchedDrawer; }, lastRestore: function () { return lastRestore; }, sheet: openSheet, add: addItems }; // sheet/add: card quick add (runit-card-quick-add.js)

  function boot() {
    watchDrawer();
    setupSizeFirst();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
